/* eslint-disable prefer-template */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

export const API = 'invoice';
export const name = 'invoiceListSlice';

export const emptyItem = () => ({
  title: '',
  description: '',
  quantity: 1,
  price: 0,
});

export const sampleInvoice = {
  id: 1,
  invoice_number: 'INV-1990',
  status: 'pending',
  create_date: '2026-09-01',
  turnover_date: '2026-09-10',
  due_date: '2026-09-20',
  from_name: 'Info Sistem',
  from_address: 'Kralja Petra 10, Beograd',
  from_phone: '011 123 456',
  to_name: 'Zgrada Brijanova 2',
  to_address: 'Brijanova 2, Kruševac',
  to_phone: '037 111 222',
  items: [
    { title: 'Održavanje', description: 'Mesečno održavanje zgrade', quantity: 1, price: 12000 },
    { title: 'Čišćenje', description: 'Čišćenje zajedničkih prostorija', quantity: 2, price: 3500 },
  ],
  discount: 500,
  shipping: 0,
  taxes: 2400,
  notes: 'Hvala na saradnji.',
  total_amount: 20900,
};

export const emptyInvoice = () => ({
  invoice_number: '',
  status: 'draft',
  create_date: null,
  turnover_date: null,
  due_date: null,
  from_name: '',
  from_address: '',
  from_phone: '',
  to_name: '',
  to_address: '',
  to_phone: '',
  items: [emptyItem()],
  discount: 0,
  shipping: 0,
  taxes: 0,
  notes: '',
});

interface InitState {
  isLoading: boolean,
  isSaving: boolean,
  show: boolean,
  showPreview: boolean,
  id: number | null,
  items: Array<any>,
  details: any,
}

function NewReducer() {
  const initialState: InitState = {
    isLoading: false,
    isSaving: false,
    show: false,
    showPreview: false,
    id: null,
    items: [sampleInvoice],
    details: null,
  };

  const reducers = {
    resetSlice: () => initialState,
    setShow: (state: InitState, action: PayloadAction<{ show: boolean, id: number | null }>) => {
      state.show = action.payload.show;
      state.id = action.payload.id;
      if (!action.payload.show) {
        state.details = null;
      }
    },
    setShowPreview: (state: InitState, action: PayloadAction<{ show: boolean, id: number | null }>) => {
      state.showPreview = action.payload.show;
      state.id = action.payload.id;
      if (!action.payload.show) {
        state.details = null;
      }
    },
    startRead: (state: InitState) => {
      state.isLoading = true;
    },
    finishRead: (state: InitState, action: PayloadAction<any>) => {
      state.items = action.payload || [];
      state.isLoading = false;
    },
    startDetails: (state: InitState) => {
      state.isLoading = true;
    },
    finishDetails: (state: InitState, action: PayloadAction<any>) => {
      state.details = action.payload;
      state.isLoading = false;
    },
    startSave: (state: InitState) => {
      state.isSaving = true;
    },
    finishSave: (state: InitState) => {
      state.isSaving = false;
    },
  };

  const slice = createSlice({
    name,
    initialState,
    reducers,
  });

  const { actions } = slice;

  const readRows = (payload: any) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (payload && Array.isArray(payload.data)) {
      return payload.data;
    }

    return [];
  };

  const apis = {
    callReadApi: (callback: (state: boolean, data: any, message: string) => void) => async (dispatch: any) => {
      dispatch(actions.startRead());

      await axios.get(API).then((result: any) => {
        const { data, message } = result.data;
        const rows = readRows(data);
        const withSample = rows.some((row: any) => row.id === sampleInvoice.id) ? rows : [sampleInvoice, ...rows];

        callback(true, withSample, message);
        dispatch(actions.finishRead(withSample));
      }).catch((error: any) => {
        const { message } = error;

        callback(false, null, message);
        dispatch(actions.finishRead([sampleInvoice]));
      });
    },

    callDetailsApi: (id: number | string, callback: (state: boolean, data: any, message: string) => void) => async (dispatch: any) => {
      if (Number(id) === sampleInvoice.id) {
        callback(true, sampleInvoice, '');
        dispatch(actions.finishDetails(sampleInvoice));
        return;
      }

      dispatch(actions.startDetails());

      await axios.get(API + '/' + id).then((result: any) => {
        const { data, message } = result.data;

        callback(true, data, message);
        dispatch(actions.finishDetails(data));
      }).catch((error: any) => {
        const { message } = error;

        callback(false, null, message);
        dispatch(actions.finishDetails(null));
      });
    },

    callSaveApi: (params: any, callback: (state: boolean, data: any, message: string) => void) => async (dispatch: any, getState: any) => {
      dispatch(actions.startSave());

      const { id } = getState()[name];
      const request = id ? axios.put(API + '/' + id, params) : axios.post(API, params);

      await request.then((result: any) => {
        const { data, message } = result.data;

        callback(true, data, message);
        dispatch(actions.finishSave());
      }).catch((error: any) => {
        const { message } = error;

        callback(false, null, message);
        dispatch(actions.finishSave());
      });
    },
  };

  return {
    reducer: slice.reducer,
    ...actions,
    ...apis,
  };
}

export default NewReducer();
